import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SiteHeader from '../components/SiteHeader';
import CardImage from '../components/CardImage';
import { decksApi } from '../api/decksApi';
import { friendsApi } from '../api/friendsApi';
import FriendsPanel from '../components/FriendsPanel';
import { useAuth } from '../auth/AuthContext';
import { useCardImage } from '../api/cardCache';
import { cardImageUrl } from '../config';
import type { DeckSummary } from '../../../shared/types';

const PRIVACY_LABEL: Record<string, string> = {
  public: 'Public', unlisted: 'Unlisted', friends: 'Friends', private: 'Private',
};

export function DeckRow({ deck, showAuthor }: { deck: DeckSummary; showAuthor?: boolean }) {
  const image = useCardImage(deck.coverCard);
  const navigate = useNavigate();
  return (
    <Link to={`/deck/${deck.id}/${deck.slug}`} className="deck-row">
      <div className="deck-row-cover"><CardImage src={cardImageUrl(image)} alt={deck.title} /></div>
      <div className="deck-row-body">
        <div className="deck-row-title">{deck.title}</div>
        <div className="deck-row-meta muted">
          <span className={`priv-badge priv-${deck.privacy}`}>{PRIVACY_LABEL[deck.privacy]}</span>
          {showAuthor && (
            <span className="deck-owner-link" onClick={e => { e.preventDefault(); e.stopPropagation(); navigate(`/user/${deck.ownerId}`); }}>
              by {deck.ownerName}
            </span>
          )}
          <span>Main {deck.counts.main}</span>
          {deck.counts.extra > 0 && <span>· Extra {deck.counts.extra}</span>}
        </div>
      </div>
    </Link>
  );
}

type Tab = 'mine' | 'friends' | 'public';

export default function DecksPage() {
  const { user, loading, getToken, refresh } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('mine');
  const [mine, setMine] = useState<DeckSummary[] | null>(null);
  const [publicDecks, setPublicDecks] = useState<DeckSummary[] | null>(null);
  const [friendsDecks, setFriendsDecks] = useState<DeckSummary[] | null>(null);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => { decksApi.listPublic().then(setPublicDecks).catch(() => setPublicDecks([])); }, []);
  useEffect(() => {
    if (loading) return;
    if (!user) { setMine(null); setFriendsDecks(null); return; }
    decksApi.listMine(getToken, refresh).then(setMine).catch(e => setError(e instanceof Error ? e.message : 'Failed to load your decks'));
  }, [user, loading, getToken, refresh]);

  const loadFriendsDecks = useCallback(() => {
    if (!user) return;
    friendsApi.decks(getToken, refresh).then(setFriendsDecks).catch(() => setFriendsDecks([]));
  }, [user, getToken, refresh]);
  useEffect(() => { loadFriendsDecks(); }, [loadFriendsDecks]);

  async function createDeck() {
    setCreating(true); setError('');
    try {
      const deck = await decksApi.create({ title: 'Untitled Deck', privacy: 'private' }, getToken, refresh);
      navigate(`/deck/${deck.id}/${deck.slug}`);
    } catch (e) { setError(e instanceof Error ? e.message : 'Failed to create deck'); setCreating(false); }
  }

  if (!loading && !user) {
    return (
      <div>
        <SiteHeader />
        <main className="page page--wide">
          <div className="signup-cta">
            <h1>Deck Builder</h1>
            <p className="muted">
              Build, save, and share your own Speed Duel decks — with synergy suggestions powered by real decklists.{' '}
              <Link to="/register">Create a free account</Link> or <Link to="/login">sign in</Link> to start building.
            </p>
          </div>
          <h2>Public Decks</h2>
          {!publicDecks && <p className="muted">Loading…</p>}
          {publicDecks && publicDecks.length === 0 && <p className="muted">No public decks yet.</p>}
          <div className="deck-list">{publicDecks?.map(d => <DeckRow key={d.id} deck={d} showAuthor />)}</div>
        </main>
      </div>
    );
  }

  return (
    <div>
      <SiteHeader />
      <main className="page page--wide">
        <div className="toolbar" style={{ justifyContent: 'space-between' }}>
          <div className="deck-tabs">
            <button type="button" className={tab === 'mine' ? 'active' : ''} onClick={() => setTab('mine')}>My Decks</button>
            <button type="button" className={tab === 'friends' ? 'active' : ''} onClick={() => setTab('friends')}>Friends</button>
            <button type="button" className={tab === 'public' ? 'active' : ''} onClick={() => setTab('public')}>Public</button>
          </div>
          <button type="button" onClick={createDeck} disabled={creating} className="create-deck-btn">
            {creating ? 'Creating…' : '+ Create deck'}
          </button>
        </div>
        {error && <p className="error">{error}</p>}

        {tab === 'mine' && (
          <>
            {mine && mine.length === 0 && <p className="muted">No decks yet — click <strong>Create deck</strong> to build one.</p>}
            <div className="deck-list">{mine?.map(d => <DeckRow key={d.id} deck={d} />)}</div>
          </>
        )}

        {tab === 'friends' && (
          <div className="friends-layout">
            <FriendsPanel onChanged={loadFriendsDecks} />
            <div className="friends-decks">
              <h2>Friends' Decks</h2>
              {!friendsDecks && <p className="muted">Loading…</p>}
              {friendsDecks && friendsDecks.length === 0 && <p className="muted">No decks from your friends yet.</p>}
              <div className="deck-list">{friendsDecks?.map(d => <DeckRow key={d.id} deck={d} showAuthor />)}</div>
            </div>
          </div>
        )}

        {tab === 'public' && (
          <>
            {!publicDecks && <p className="muted">Loading…</p>}
            {publicDecks && publicDecks.length === 0 && <p className="muted">No public decks yet.</p>}
            <div className="deck-list">{publicDecks?.map(d => <DeckRow key={d.id} deck={d} showAuthor />)}</div>
          </>
        )}
      </main>
    </div>
  );
}
